import { createI18n } from 'vue-i18n'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, resolveLocale } from './constants'
import { createMessages } from './messages'

export const messages = createMessages(SUPPORTED_LOCALES)

const LOCALE_KEYS = ['content-language', 'contentLanguage', 'content_language', 'lang', 'locale', 'language']
const COOKIE_QUERY_KEYS = ['cookie', 'cookieData']
const REQUEST_LANGUAGE_ALIASES = {
  en: 'en',
  'en-us': 'en',
  'en_us': 'en',
  'en-gb': 'en',
  'en_gb': 'en',
  zh: 'zh',
  'zh-cn': 'zh',
  'zh_cn': 'zh',
  'zh-tw': 'zh',
  'zh_tw': 'zh',
  ja: 'ja',
  'ja-jp': 'ja',
  'ja_jp': 'ja'
}

function normalizeValue(value) {
  if (Array.isArray(value)) return normalizeValue(value[0])
  return typeof value === 'string' ? value.trim() : ''
}

function getSupportedLocale(value) {
  const normalized = normalizeValue(value)
  if (!normalized) return ''

  if (SUPPORTED_LOCALES.includes(normalized)) return normalized

  const lowercased = normalized.toLowerCase()
  const localeAliasMap = {
    'zh-cn': 'zh-CN',
    'zh_cn': 'zh-CN',
    zh: 'zh-CN',
    'en-us': 'en',
    'en_us': 'en',
    'en-gb': 'en',
    'en_gb': 'en',
    ja: 'ja',
    'ja-jp': 'ja',
    'ja_jp': 'ja',
    'zh-tw': 'zh-TW',
    'zh_tw': 'zh-TW'
  }

  return localeAliasMap[lowercased] || ''
}

function getRequestLanguage(value) {
  const normalized = normalizeValue(value)
  if (!normalized) return ''

  const locale = getSupportedLocale(normalized)
  if (locale === 'en') return 'en'
  if (locale === 'ja') return 'ja'
  if (locale === 'zh-CN' || locale === 'zh-TW') return 'zh'

  return REQUEST_LANGUAGE_ALIASES[normalized.toLowerCase()] || ''
}

function pickValueEntry(source, keys) {
  if (!source) return { key: '', value: '' }

  for (const key of keys) {
    const value = normalizeValue(source[key])
    if (value) return { key, value }
  }

  return { key: '', value: '' }
}

function createSearchParams(search) {
  try {
    return new URLSearchParams(typeof search === 'string' ? search : '')
  } catch {
    return new URLSearchParams()
  }
}

function parseJsonRecord(rawValue) {
  const value = normalizeValue(rawValue)
  if (!value) return null

  const candidates = [value]

  try {
    const decoded = decodeURIComponent(value)
    if (decoded && decoded !== value) candidates.unshift(decoded)
  } catch {
    // Ignore malformed URI sequences and continue with the raw value.
  }

  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate)
      if (parsed && typeof parsed === 'object') return parsed
    } catch {
      // Ignore non-JSON values and continue probing.
    }
  }

  return null
}

function inspectLocaleValue(rawValue, source) {
  const normalizedRawValue = normalizeValue(rawValue)
  const uiLocale = getSupportedLocale(normalizedRawValue)
  const requestLanguage = getRequestLanguage(normalizedRawValue)
  const matched = Boolean(uiLocale || requestLanguage)

  return {
    source,
    rawValue: normalizedRawValue,
    uiLocale,
    requestLanguage,
    matched
  }
}

function createLocaleCandidate(rawValue, source) {
  const inspection = inspectLocaleValue(rawValue, source)
  if (!inspection.matched) return null

  return {
    rawValue: inspection.rawValue,
    locale: inspection.uiLocale || DEFAULT_LOCALE,
    uiLocale: inspection.uiLocale || DEFAULT_LOCALE,
    requestLanguage: inspection.requestLanguage || getRequestLanguage(inspection.uiLocale) || getRequestLanguage(DEFAULT_LOCALE) || 'en',
    source
  }
}

function collectRecordCandidates(record, sourcePrefix = '') {
  return LOCALE_KEYS.map((key) => inspectLocaleValue(record?.[key], sourcePrefix ? `${sourcePrefix}.${key}` : key))
}

function extractLocaleFromRecord(record, sourcePrefix = '') {
  return collectRecordCandidates(record, sourcePrefix).find((candidate) => candidate.matched) || null
}

function extractLocaleFromCookieString(cookieString) {
  const rawCookie = normalizeValue(cookieString)
  if (!rawCookie) return null

  const parsedCookie = parseJsonRecord(rawCookie)
  const jsonLocale = extractLocaleFromRecord(parsedCookie, 'document.cookie.json')
  if (jsonLocale) return jsonLocale

  for (const segment of rawCookie.split(';')) {
    const trimmed = segment.trim()
    if (!trimmed) continue

    const separatorIndex = trimmed.indexOf('=')
    if (separatorIndex === -1) continue

    const name = trimmed.slice(0, separatorIndex).trim()
    if (!LOCALE_KEYS.includes(name)) continue

    const rawValue = trimmed.slice(separatorIndex + 1).trim().replace(/^"(.*)"$/, '$1')
    const candidate = createLocaleCandidate(rawValue, `document.cookie.${name}`)
    if (candidate) return candidate
  }

  return null
}

function collectCookieSegments(cookieString) {
  const rawCookie = normalizeValue(cookieString)
  if (!rawCookie) return []

  return rawCookie.split(';').map((segment) => {
    const rawSegment = segment.trim()
    if (!rawSegment) {
      return {
        rawSegment: '',
        key: '',
        rawValue: '',
        isLocaleKey: false,
        matched: false,
        uiLocale: '',
        requestLanguage: ''
      }
    }

    const separatorIndex = rawSegment.indexOf('=')
    if (separatorIndex === -1) {
      return {
        rawSegment,
        key: rawSegment,
        rawValue: '',
        isLocaleKey: false,
        matched: false,
        uiLocale: '',
        requestLanguage: ''
      }
    }

    const key = rawSegment.slice(0, separatorIndex).trim()
    const rawValue = rawSegment.slice(separatorIndex + 1).trim().replace(/^"(.*)"$/, '$1')
    const inspection = inspectLocaleValue(rawValue, `document.cookie.${key}`)

    return {
      rawSegment,
      key,
      rawValue,
      isLocaleKey: LOCALE_KEYS.includes(key),
      matched: inspection.matched,
      uiLocale: inspection.uiLocale,
      requestLanguage: inspection.requestLanguage
    }
  }).filter((segment) => segment.rawSegment)
}

function createTraceEntry(stage, candidate, extra = {}) {
  return {
    stage,
    matched: Boolean(candidate),
    source: candidate?.source || '',
    rawValue: candidate?.rawValue || '',
    uiLocale: candidate?.uiLocale || '',
    requestLanguage: candidate?.requestLanguage || '',
    ...extra
  }
}

function resolveDefaultLocale(defaultLocale) {
  const normalizedDefaultLocale = getSupportedLocale(defaultLocale) || DEFAULT_LOCALE
  return createLocaleCandidate(normalizedDefaultLocale, 'default') || {
    rawValue: normalizedDefaultLocale,
    locale: normalizedDefaultLocale,
    uiLocale: normalizedDefaultLocale,
    requestLanguage: getRequestLanguage(normalizedDefaultLocale) || 'en',
    source: 'default'
  }
}

function resolveCookiePayloadLocale(source, sourceLabel) {
  const cookieEntry = pickValueEntry(source, COOKIE_QUERY_KEYS)
  if (!cookieEntry.value) return { candidate: null, trace: createTraceEntry(sourceLabel, null) }

  const parsedPayload = parseJsonRecord(cookieEntry.value)
  const candidate = extractLocaleFromRecord(parsedPayload, `${sourceLabel}.${cookieEntry.key}`)

  return {
    candidate,
    trace: createTraceEntry(sourceLabel, candidate, {
      cookieKey: cookieEntry.key,
      payloadPresent: Boolean(parsedPayload)
    })
  }
}

export function resolveLocaleContext(routeQuery, options = {}) {
  const fallback = resolveDefaultLocale(options.defaultLocale)
  const search = options.search ?? (typeof window !== 'undefined' ? window.location.search : '')
  const cookieString = options.cookie ?? (typeof document !== 'undefined' ? document.cookie : '')
  const searchParams = createSearchParams(search)
  const searchRecord = Object.fromEntries(searchParams.entries())
  const trace = []
  const rawCookie = normalizeValue(cookieString)
  const routeCandidates = collectRecordCandidates(routeQuery, 'routeQuery')
  const searchCandidates = collectRecordCandidates(searchRecord, 'search')
  const routeCookieEntry = pickValueEntry(routeQuery, COOKIE_QUERY_KEYS)
  const routeCookiePayload = parseJsonRecord(routeCookieEntry.value)
  const routeCookieCandidates = collectRecordCandidates(routeCookiePayload, routeCookieEntry.key ? `routeQuery.${routeCookieEntry.key}` : 'routeQuery')
  const searchCookieEntry = pickValueEntry(searchRecord, COOKIE_QUERY_KEYS)
  const searchCookiePayload = parseJsonRecord(searchCookieEntry.value)
  const searchCookieCandidates = collectRecordCandidates(searchCookiePayload, searchCookieEntry.key ? `search.${searchCookieEntry.key}` : 'search')
  const cookieJsonRecord = parseJsonRecord(rawCookie)
  const cookieJsonCandidates = collectRecordCandidates(cookieJsonRecord, 'document.cookie.json')
  const cookieSegments = collectCookieSegments(rawCookie)
  let result = null
  let fallbackReason = ''

  const routeCookiePayloadResult = resolveCookiePayloadLocale(routeQuery, 'routeQuery')
  trace.push(routeCookiePayloadResult.trace)
  if (routeCookiePayloadResult.candidate) {
    result = { ...routeCookiePayloadResult.candidate, trace }
  }

  if (!result) {
    const searchCookiePayloadResult = resolveCookiePayloadLocale(searchRecord, 'search')
    trace.push(searchCookiePayloadResult.trace)
    if (searchCookiePayloadResult.candidate) {
      result = { ...searchCookiePayloadResult.candidate, trace }
    }
  }

  if (!result) {
    const cookieCandidate = extractLocaleFromCookieString(cookieString)
    trace.push(createTraceEntry('document.cookie', cookieCandidate, {
      cookiePresent: Boolean(rawCookie)
    }))
    if (cookieCandidate) {
      result = { ...cookieCandidate, trace }
    }
  }

  if (!result) {
    const routeCandidate = routeCandidates.find((candidate) => candidate.matched) || null
    trace.push(createTraceEntry('routeQuery', routeCandidate))
    if (routeCandidate) {
      result = { ...routeCandidate, trace }
    }
  }

  if (!result) {
    const searchCandidate = searchCandidates.find((candidate) => candidate.matched) || null
    trace.push(createTraceEntry('search', searchCandidate))
    if (searchCandidate) {
      result = { ...searchCandidate, trace }
    }
  }

  if (!result) {
    fallbackReason = 'No locale key matched in query cookie payload, search cookie payload, document.cookie, route query, or search params.'
    trace.push(createTraceEntry('default', fallback))
    result = { ...fallback, trace }
  }

  const cookieMatchedSegment = cookieSegments.find((segment) => segment.matched) || null
  const matchedCookieValue = result.source.startsWith('document.cookie.')
    ? normalizeValue(result.rawValue)
    : routeCookiePayloadResult.candidate?.rawValue ||
      searchCookieCandidates.find((candidate) => candidate.matched)?.rawValue ||
      cookieJsonCandidates.find((candidate) => candidate.matched)?.rawValue ||
      cookieMatchedSegment?.rawValue ||
      ''
  const matchedCookieKey = result.source.startsWith('document.cookie.')
    ? result.source.replace('document.cookie.', '')
    : routeCookieEntry.key || searchCookieEntry.key || cookieMatchedSegment?.key || ''

  return {
    ...result,
    debugDetails: {
      rawCookie,
      cookieSegments,
      matchedCookieKey,
      matchedCookieValue,
      cookieJsonParsed: Boolean(cookieJsonRecord),
      cookieJsonRecord,
      cookieJsonCandidates,
      routeCookiePayload: {
        cookieKey: routeCookieEntry.key,
        rawValue: routeCookieEntry.value,
        parsedRecord: routeCookiePayload,
        candidates: routeCookieCandidates
      },
      searchCookiePayload: {
        cookieKey: searchCookieEntry.key,
        rawValue: searchCookieEntry.value,
        parsedRecord: searchCookiePayload,
        candidates: searchCookieCandidates
      },
      routeCandidates,
      searchCandidates,
      finalSource: result.source,
      finalUiLocale: result.uiLocale,
      finalRequestLanguage: result.requestLanguage,
      fallbackReason
    }
  }
}

export function detectLocale(routeQuery, options = {}) {
  return resolveLocaleContext(routeQuery, options).uiLocale
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages
})

export function syncLocale(locale) {
  const nextLocale = resolveLocale(locale)
  if (i18n.global.locale.value !== nextLocale) {
    i18n.global.locale.value = nextLocale
  }
  return nextLocale
}

export { DEFAULT_LOCALE, LOCALE_LABELS_ZH, SUPPORTED_LOCALES, resolveLocale } from './constants'
